import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Icon,
  message,
  Upload,
  Tooltip,
  Spin,
  Card,
  Progress,
  Alert,
  Avatar,
  Modal
} from 'antd'
import {
  listAIVoices,
  uploadCustomVoice,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import StepTitle from '../StepTitle'
import './Voiceover.scss'

const AUDIO_PREVIEW_BASE_URL = 'https://video-generate.sharelookapp.link/audio/'

const Voiceover = ({
  formData,
  setFormData,
  onValidationChange,
  voices,
  setVoices,
  onVoiceChange
}) => {
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingVoiceId, setPendingVoiceId] = useState(null)
  const audioRef = useRef(null)

  // Get selected voice from formData
  const selectedVoice = formData?.selectedVoice || null

  useEffect(() => {
    if (voices.length === 0) {
      fetchVoices()
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Clear error when closing upload panel
  useEffect(() => {
    if (!showUpload) {
      setUploadError(null)
    }
  }, [showUpload])

  // Validate step when voice is selected
  const onValidationChangeRef = useRef(onValidationChange)

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange
  }, [onValidationChange])

  // Validate step when voice is selected
  useEffect(() => {
    if (selectedVoice && onValidationChangeRef.current) {
      onValidationChangeRef.current(true)
    }
  }, [selectedVoice])

  const waitForTaskSuccess = async taskId => {
    if (!taskId) throw new Error('Missing task id')

    const maxAttempts = 100
    const delayMs = 3000

    await new Promise(resolve => setTimeout(resolve, 2000))

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse?.data || null
        const status = data?.status || null

        if (status === 'success') {
          const output = data?.output || data?.audio_url || data?.result || null
          if (output?.success === false || output?.error) {
            const errorMsg = output?.error || 'Operation failed'
            throw new Error(errorMsg)
          }
          return output
        }

        if (status === 'failed' || status === 'error') {
          const errorMsg = data?.output?.error || data?.error || 'Task failed'
          throw new Error(errorMsg)
        }
      } catch (err) {
        if (err.message === 'Task failed' || attempt === maxAttempts - 1)
          throw err
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices()
      const task_id = res?.data?.task_id

      if (!task_id) {
        console.error('No task_id received from listAIVoices')
        setVoices([])
        return
      }

      const data = await waitForTaskSuccess(task_id)

      if (!data) {
        console.error('No data received from task')
        setVoices([])
        return
      }

      const defaultVoices = data?.default_voices || []
      const uploadedVoices = data?.uploaded_voices || []

      const allVoices = [
        ...defaultVoices.map((voice, index) => ({
          id: voice,
          name: `Voice ${index + 1}`,
          type: 'default'
        })),
        ...uploadedVoices.map(voice => ({
          id: voice,
          name: voice,
          type: 'uploaded'
        }))
      ]

      setVoices(allVoices)
    } catch (err) {
      console.error('Failed to fetch voices', err)
      message.error('Failed to load voices')
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }

  const handleSelectVoice = voiceId => {
    // If deselecting the current voice
    if (selectedVoice === voiceId) {
      setFormData(prev => ({
        ...prev,
        selectedVoice: null
      }))
      return
    }

    // Check if we have generated content that would be invalidated
    const hasGeneratedContent =
      formData.voiceoverAudio ||
      (formData.shotList &&
        formData.shotList.some(
          scene =>
            scene.audio_link ||
            scene.audio_duration ||
            scene.shots.some(shot => shot.vidSrc || shot.video_duration)
        ))

    if (hasGeneratedContent) {
      setPendingVoiceId(voiceId)
      setShowConfirmationModal(true)
    } else {
      applyVoiceSelection(voiceId)
    }
  }

  const handleConfirmChange = () => {
    if (pendingVoiceId) {
      applyVoiceSelection(pendingVoiceId)
    }
    setShowConfirmationModal(false)
    setPendingVoiceId(null)
  }

  const handleCancelChange = () => {
    setShowConfirmationModal(false)
    setPendingVoiceId(null)
  }

  const applyVoiceSelection = voiceId => {
    if (onVoiceChange) {
      onVoiceChange(voiceId)
    } else {
      setFormData(prev => ({
        ...prev,
        selectedVoice: voiceId,
        voiceoverAudio: null,
        shotList: (prev.shotList || []).map(scene => ({
          ...scene,
          shots: scene.shots.map(shot => ({
            ...shot,
            audio_duration: null,
            audio_link: null
          }))
        }))
      }))
    }
  }

  const handlePlayPreview = voiceId => {
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      const audio = new Audio(`${AUDIO_PREVIEW_BASE_URL}${voiceId}`)
      audio.play().catch(err => {
        console.error('Audio preview failed:', err)
        message.error('Failed to play audio preview')
      })
      audio.onended = () => setPlayingVoice(null)
      audioRef.current = audio
      setPlayingVoice(voiceId)
    }
  }

  const handleUploadVoice = async options => {
    const { file, onSuccess, onError } = options

    const uploadFormData = new FormData()
    uploadFormData.append('voice_file', file)
    uploadFormData.append('voice_name', file.name.replace(/\.[^/.]+$/, ''))

    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      await uploadCustomVoice(uploadFormData)

      clearInterval(progressInterval)
      setUploadProgress(100)

      message.success('Voice uploaded successfully!')
      setShowUpload(false)

      // Refresh voice list
      await fetchVoices()

      if (onSuccess) onSuccess()
    } catch (err) {
      console.error('Upload failed:', err)
      let errorMsg = 'Failed to upload voice'

      if (err.response) {
        if (err.response.status === 413) {
          errorMsg =
            'File is too large. Please upload a file smaller than 50MB.'
        } else if (err.response.data && err.response.data.error) {
          errorMsg = err.response.data.error
        }
      } else if (err.message) {
        errorMsg = err.message
      }

      setUploadError(errorMsg)

      if (onError) onError(err)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Pre-upload validation
  const beforeUpload = file => {
    setUploadError(null)
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      setUploadError('File must be smaller than 50MB!')
      return false
    }
    return true
  }

  const getSelectedVoiceName = () => {
    if (!selectedVoice) return null
    const voice = voices.find(v => v.id === selectedVoice)
    return voice?.name || selectedVoice
  }

  return (
    <div className="voiceover-step">
      <StepTitle
        iconType="sound"
        title="Voiceover"
        description="Select a voice for your video narration."
      />

      <div className="voiceover-step__content">
        <div className="voiceover-step__main-layout">
          {/* Section: Custom Voices (including Upload) */}
          <div className="voiceover-step__section">
            <div className="voiceover-step__section-header">
              <h3 className="voiceover-step__section-title">Your Voices</h3>
            </div>
            <div className="voiceover-step__voice-grid">
              {/* Upload Card */}
              <div
                className={`voiceover-step__upload-card ${
                  showUpload ? 'voiceover-step__upload-card--active' : ''
                } ${uploading ? 'voiceover-step__upload-card--disabled' : ''}`}
                onClick={() => !uploading && setShowUpload(!showUpload)}
              >
                <div className="voiceover-step__upload-card-content">
                  <div className="upload-icon-wrapper">
                    <Icon type={showUpload ? 'minus' : 'plus'} />
                  </div>
                  <div className="upload-text-content">
                    <span className="main-text">Add Custom Voice</span>
                    <span className="sub-text">Upload your own audio</span>
                  </div>
                </div>
              </div>

              {voices
                .filter(v => v.type === 'uploaded')
                .map((voice, index) => (
                  <VoiceCard
                    key={voice.id}
                    voice={voice}
                    isSelected={selectedVoice === voice.id}
                    isPlaying={playingVoice === voice.id}
                    onSelect={() => handleSelectVoice(voice.id)}
                    onPlay={e => {
                      e.stopPropagation()
                      handlePlayPreview(voice.id)
                    }}
                  />
                ))}
            </div>

            {/* Inline Upload Panel */}
            {showUpload && (
              <div className="voiceover-step__upload-panel">
                {uploadError && (
                  <Alert
                    message={uploadError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setUploadError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}
                {uploading ? (
                  <div className="voiceover-step__upload-loading">
                    <Spin
                      indicator={
                        <Icon type="loading" style={{ fontSize: 24 }} spin />
                      }
                      tip="Uploading voice..."
                    />
                    <Progress percent={uploadProgress} size="small" />
                  </div>
                ) : (
                  <Upload.Dragger
                    accept=".wav,.mp3,.ogg,.m4a,.flac"
                    customRequest={handleUploadVoice}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    disabled={uploading}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to upload
                    </p>
                    <p className="ant-upload-hint">
                      WAV, MP3, OGG, M4A, FLAC (Max 50MB)
                    </p>
                  </Upload.Dragger>
                )}
              </div>
            )}
          </div>

          <div className="voiceover-step__divider" />

          {/* Section: System Voices */}
          <div className="voiceover-step__section">
            <div className="voiceover-step__section-header">
              <h3 className="voiceover-step__section-title">System Voices</h3>
              <Button
                type="link"
                icon="reload"
                onClick={fetchVoices}
                loading={loadingVoices}
                className="refresh-btn"
              >
                Refresh
              </Button>
            </div>

            {loadingVoices ? (
              <div className="voiceover-step__loading">
                <Spin size="default" />
              </div>
            ) : (
              <div className="voiceover-step__voice-grid">
                {voices
                  .filter(v => v.type !== 'uploaded')
                  .map((voice, index) => (
                    <VoiceCard
                      key={voice.id}
                      voice={voice}
                      isSelected={selectedVoice === voice.id}
                      isPlaying={playingVoice === voice.id}
                      onSelect={() => handleSelectVoice(voice.id)}
                      onPlay={e => {
                        e.stopPropagation()
                        handlePlayPreview(voice.id)
                      }}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voice Change Confirmation Modal */}
      <Modal
        visible={showConfirmationModal}
        onCancel={handleCancelChange}
        footer={null}
        centered
        width={520}
        className="voiceover-change-modal"
      >
        <div className="voiceover-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Change Voice Narrator?</h3>
          <p className="modal-description">
            Changing the voice will affect the timing of your video. This
            requires clearing some generated assets.
          </p>
          <div className="modal-options">
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="sound" />
              </div>
              <div className="option-content">
                <h4 className="option-title">What will be cleared:</h4>
                <p className="option-description">
                  • <strong>Audio:</strong> Will be regenerated with new voice.
                  <br />• <strong>Videos:</strong> Videos with different audio
                  duration will be regenerated.
                  <br />
                  <span style={{ color: '#5057d5', fontWeight: 500 }}>
                    • <strong>Images:</strong> Will be preserved (Safe).
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelChange} size="large">
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmChange}
              size="large"
              icon="check"
            >
              Confirm Change
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Sub-component for cleaner code
const VoiceCard = ({ voice, isSelected, isPlaying, onSelect, onPlay }) => {
  const isUploaded = voice.type === 'uploaded'

  return (
    <div
      className={`voiceover-card ${
        isSelected ? 'voiceover-card--selected' : ''
      } ${isUploaded ? 'voiceover-card--uploaded' : ''}`}
      onClick={onSelect}
    >
      <div className="voiceover-card__visual">
        <Avatar
          size={40}
          icon={isUploaded ? 'user' : 'customer-service'}
          className={`voiceover-card__avatar ${
            isSelected ? 'voiceover-card__avatar--selected' : ''
          }`}
        />
        <div
          className={`voiceover-card__radio ${
            isSelected ? 'voiceover-card__radio--checked' : ''
          }`}
        >
          {isSelected && <div className="voiceover-card__radio-dot" />}
        </div>
      </div>

      <div className="voiceover-card__content">
        <span className="voiceover-card__name">{voice.name}</span>
        <span className="voiceover-card__type">
          {isUploaded ? 'Custom' : 'System'} Voice
        </span>
      </div>

      <div className="voiceover-card__actions">
        <button
          className={`voiceover-card__play-btn ${
            isPlaying ? 'voiceover-card__play-btn--playing' : ''
          }`}
          onClick={onPlay}
        >
          <Icon type={isPlaying ? 'pause' : 'caret-right'} />
        </button>
      </div>
    </div>
  )
}

export default Voiceover
